// src/App.js

import React, { useState, useEffect } from "react";
import { Canvas } from "@react-three/fiber";

import LuxuryRoom from "./components/LuxuryRoom";
import AvatarEditorPanel from "./components/AvatarEditorPanel";
import BrowsePanel from "./components/BrowsePanel";
import FittingRoomPanel from "./components/FittingRoomPanel";
import { defaultAvatar } from "./avatar/defaultAvatar";
import "./App.css";
import DepthOcclusionProvider from "./components/DepthOcclusionProvider";
import EditPanel from "./components/EditPanel";
import { useSearchParams, useLocation, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import MyWardrobePanel from "./components/MyWardrobePanel";
import * as THREE from "three";
import { loadGarmentMetrics } from "./rig/garmentMetrics";
import { BODY_REFS } from "./rig/bodyRefs";

import PublicLayout from "./pages/public/PublicLayout";
import MoodsIndex from "./pages/public/MoodsIndex";
import MoodPage from "./pages/public/MoodPage";
import About from "./pages/public/About";

import BrowsePage from "./pages/public/BrowsePage";
import ProductDetailPage from "./pages/public/ProductDetailPage";
import { MOODBOARDS } from "./content/moodboards";
import { getActiveEditId, setActiveEditId } from "./state/activeEditStore";
import EditsPage from "./pages/public/EditsPage";
import EditViewerPage from "./pages/public/EditViewerPage";
import {
  getEditItems as getEditItemsStore,
  setEditItems as setEditItemsStore,
  addToEdit,
} from "./state/editStore";
import MerchantLanding from "./pages/public/MerchantLanding";
import { DEMO_PRODUCTS } from "./catalog/products";
import {
  listFittingQueue,
  addToFittingQueue,
  removeFromFittingQueue,
  FITTING_QUEUE_CHANGED_EVENT,
} from "./state/fittingRoomQueueStore";
import AvatarEditorPage from "./pages/public/AvatarEditorPage";
import Landing from "./pages/public/Landing";
import MerchantSignup from "./pages/merchant/MerchantSignup";
import MerchantLogin from "./pages/merchant/MerchantLogin";
import MerchantCatalogManager from "./pages/merchant/MerchantCatalogManager";
import MerchantImportCsv from "./pages/merchant/MerchantImportCsv";
import MerchantBoutiqueDetails from "./pages/merchant/MerchantBoutiqueDetails";
import OnboardingGuard from "./components/merchant/OnboardingGuard";
import MerchantAuthGuard from "./components/merchant/MerchantAuthGuard";
import ShopperLogin from "./pages/auth/ShopperLogin";
import ShopperSignup from "./pages/auth/ShopperSignup";
import RequireShopperAuth from "./components/auth/RequireShopperAuth";
import { getShopperToken } from "./lib/api";
import { fetchAvatar, saveAvatar } from "./lib/avatar";
import { useSession } from "./hooks/useSession";
import { createEditMeta, getEditMeta } from "./state/editsStore";
import ShopperAccountPage from "./components/account/ShopperAccountPage";
import MerchantSettingsPage from "./pages/merchant/MerchantSettingsPage";
import { logTryOnStarted } from "./lib/interactionEventsApi";
import BagPage from "./pages/public/BagPage";
import Login from "./pages/auth/Login";
import VeyaTeamLogin from "./pages/veyaTeam/VeyaTeamLogin";
import VeyaTeamAuthGuard from "./pages/veyaTeam/VeyaTeamAuthGuard";
import VeyaTeamShell from "./pages/veyaTeam/VeyaTeamShell";
import VeyaTeamEdits from "./pages/veyaTeam/VeyaTeamEdits";
import VeyaTeamMerchants from "./pages/veyaTeam/VeyaTeamMerchants";
import VeyaTeamActivity from "./pages/veyaTeam/VeyaTeamActivity";
import LegalIndex from "./pages/public/legal/LegalIndex";
import PrivacyPolicy from "./pages/public/legal/PrivacyPolicy";
import TermsOfUse from "./pages/public/legal/TermsOfUse";
import MerchantTerms from "./pages/public/legal/MerchantTerms";
import CookieNotice from "./pages/public/legal/CookieNotice";
import LicenseNotices from "./pages/public/legal/LicenseNotices";
import VerifyEmailSent from "./pages/auth/VerifyEmailSent";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import MerchantInfoPage from "./pages/public/MerchantInfoPage";

function VeyaIntroModal({ open, onClose, onStartBrowsing }) {
const tutorials = {
  edits: [
    "/tutorial/edits-1.webp",
    "/tutorial/edits-2.webp",
    "/tutorial/edits-3.webp",
  ],

  fittingRoom: [
    "/tutorial/fitting-room-1.webp",
    "/tutorial/fitting-room-2.webp",
  ],
};

  const [index, setIndex] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState("edits");
  const allTutorialSlides = React.useMemo(
  () => [...tutorials.edits, ...tutorials.fittingRoom],
  []
);
 
  React.useEffect(() => {
  if (!open) return;

  setIndex(0);
  setActiveTab("edits");

  for (const src of allTutorialSlides) {
    const img = new Image();
    img.src = src;
  }
}, [open, allTutorialSlides]);

  if (!open) return null;

  const slides = tutorials[activeTab];
const slide = slides[index];
const isLast = index === slides.length - 1;

  function finish() {
    localStorage.setItem("veya_intro_seen_v1", "true");
    onClose?.();
  }

  return (
    <div className="veya-intro-backdrop" role="dialog" aria-modal="true">
      <div className="veya-intro-card">
        <button
          type="button"
          className="veya-intro-close"
          onClick={finish}
          aria-label="Close intro"
        >
          ×
        </button>

<div className="veya-intro-tabs">
  <button
    type="button"
    className={activeTab === "edits" ? "is-active" : ""}
    onClick={() => {
      setActiveTab("edits");
      setIndex(0);
    }}
  >
    Edits
  </button>

  <button
    type="button"
    className={activeTab === "fittingRoom" ? "is-active" : ""}
    onClick={() => {
      setActiveTab("fittingRoom");
      setIndex(0);
    }}
  >
    Fitting Room
  </button>
</div>

<div className="veya-intro-visual">
  <img
    key={slide}
    src={slide}
    alt=""
    className="veya-intro-image"
  />
</div>

<div className="veya-intro-content">

          <div className="veya-intro-dots">
            {slides.map((_, dotIndex) => (
              <button
                key={dotIndex}
                type="button"
                className={dotIndex === index ? "is-active" : ""}
                onClick={() => setIndex(dotIndex)}
                aria-label={`Go to slide ${dotIndex + 1}`}
              />
            ))}
          </div>

          <div className="veya-intro-actions">
            <button type="button" className="ghost-btn" onClick={finish}>
              Skip
            </button>

            {index > 0 && (
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
              >
                Back
              </button>
            )}

            <button
  type="button"
  className="gold-btn"
  onClick={() => {
    if (isLast && activeTab === "edits") {
      setActiveTab("fittingRoom");
      setIndex(0);
      return;
    }

    if (isLast) {
      finish();
      return;
    }

    setIndex((i) => Math.min(slides.length - 1, i + 1));
  }}
>
  {isLast && activeTab === "edits" ? "Next: Fitting Room" : isLast ? "Done" : "Next"}
</button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function App() {
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const navigate = useNavigate();
  const { isShopper, isVeyaTeam } = useSession();
  const [introOpen, setIntroOpen] = useState(false);
  const reopenIntro = () => {
  setIntroOpen(true);
};

useEffect(() => {
  const seen = localStorage.getItem("veya_intro_seen_v1");

  const shouldShowIntro =
    !seen &&
    location.pathname.startsWith("/browse");

  if (shouldShowIntro) {
    setIntroOpen(true);
  }
}, [location.pathname]);

const isPublic =
  location.pathname === "/" ||
  location.pathname.startsWith("/browse") ||
  location.pathname.startsWith("/moods") ||
  location.pathname.startsWith("/merchant") ||
  location.pathname.startsWith("/about") ||
  location.pathname.startsWith("/edits") ||
  location.pathname.startsWith("/avatar") ||
  location.pathname.startsWith("/account") ||
  location.pathname.startsWith("/login") ||
  location.pathname.startsWith("/bag") ||
  location.pathname.startsWith("/products") ||
  location.pathname.startsWith("/veya-team") ||
  location.pathname.startsWith("/legal") ||
  location.pathname.startsWith("/signup") ||
  location.pathname.startsWith("/merchant") ||
    location.pathname.startsWith("/verify-email");

  // ---------- UI panels ----------
    // ---------- UI shell (single active drawer) ----------
  const [menuOpen, setMenuOpen] = useState(false);

  // Two independent drawers (left + right)
const [leftDrawer, setLeftDrawer] = useState(null);   // null | "browse" | "wardrobe" | "avatar" | "scan"
const [rightDrawer, setRightDrawer] = useState(null); // null | "fittingRoom" | "edit"


  const [backView, setBackView] = useState(false);
  const [view, setView] = useState("front"); // "front" | "back"
  const toggleView = () => setView((v) => (v === "front" ? "back" : "front"));

const openDrawer = (name) => {
  if (name === "avatar" && !isShopper && !isVeyaTeam) {
    setMenuOpen(false);
    navigate("/login", { state: { from: "/avatar" } });
    return;
  }

  if (name === "fittingRoom" || name === "edit") {
  if (name === "edit") ensureActiveEdit();
  setRightDrawer(name);

  if (window.innerWidth <= 768) {
    setLeftDrawer(null);
  }
} else {
  setLeftDrawer(name);

  if (window.innerWidth <= 768) {
    setRightDrawer(null);
  }
}

  setMenuOpen(false);
};

const closeLeftDrawer = () => {
  setLeftDrawer(null);
  setMenuOpen(false);
};

const closeRightDrawer = () => {
  setRightDrawer(null);
  setMenuOpen(false);
};

  React.useEffect(() => {
  if (searchParams.get("browse") === "1") {
    setLeftDrawer("browse");
  }
}, [searchParams]);

React.useEffect(() => {
  const openMood = searchParams.get("openedit") === "1";
  const templateSlug = searchParams.get("template");

  if (!openMood) return;

  // open the right drawer
  setRightDrawer("edit");

  // load template pins if provided
  if (templateSlug) {
    const tpl = MOODBOARDS.find((m) => m.slug === templateSlug);
    if (tpl?.pins?.length) {
  const editId = ensureActiveEdit();

  const hydrated = tpl.pins.map((p, i) => ({
    id: `${Date.now()}_${Math.random().toString(16).slice(2)}_${i}`,
    createdAt: Date.now() + i,
    ...p,
    sourceUrl: p.sourceUrl ?? null,
  }));

  setEditItemsStore(hydrated, editId);
}
  }
}, [searchParams]);

useEffect(() => {
  const handler = (ev) => {
    const detail = ev?.detail || {};

    navigate("/login", {
      state: {
        from: location.pathname,
        authMessage:
          detail.message || "To save this edit, create an account or log in.",
        authReason: detail.reason || "save_edit",
        editId: detail.editId || null,
      },
    });
  };

  window.addEventListener("veya_auth_required", handler);
  return () => window.removeEventListener("veya_auth_required", handler);
}, [navigate, location.pathname]);

const [garmentMetricsMap, setGarmentMetricsMap] = useState(null);

React.useEffect(() => {
  loadGarmentMetrics()
    .then(setGarmentMetricsMap)
    .catch((e) => console.error("[garmentMetrics] load failed", e));
}, []);


  // ---------- Avatar ----------
const [avatarParams, setAvatarParams] = useState(() => {
  try {
    const saved = localStorage.getItem("veya_avatar_v1");
    return saved ? JSON.parse(saved) : defaultAvatar;
  } catch {
    return defaultAvatar;
  }
});

useEffect(() => {
  localStorage.setItem("veya_avatar_v1", JSON.stringify(avatarParams));

  if (getShopperToken()) {
    saveAvatar(avatarParams).catch((e) => {
      console.warn("saveAvatar failed", e);
    });
  }
}, [avatarParams]);

useEffect(() => {
  let cancelled = false;

  async function hydrateShopperAvatar() {
    if (!getShopperToken()) return;

    try {
      const avatar = await fetchAvatar();
      if (!avatar || cancelled) return;

      localStorage.setItem("veya_avatar_v1", JSON.stringify(avatar));
      setAvatarParams(avatar);
    } catch (e) {
      console.warn("fetchAvatar failed", e);
    }
  }

  hydrateShopperAvatar();

  return () => {
    cancelled = true;
  };
}, []);

  // ---------- Fitting room ----------
  const [fittingRoomItems, setFittingRoomItems] = useState([]);
  
  const hydrateFittingQueue = React.useCallback(() => {
    const q = listFittingQueue() || [];

    const hydrated = q
      .map((row) => {
        if (row?.item?.id) return row.item;
        return null;
      })
      .filter(Boolean);

    setFittingRoomItems(hydrated);
  }, []);
  // Initial load + live updates when queue changes
  React.useEffect(() => {
    hydrateFittingQueue();
    window.addEventListener(FITTING_QUEUE_CHANGED_EVENT, hydrateFittingQueue);
    return () => window.removeEventListener(FITTING_QUEUE_CHANGED_EVENT, hydrateFittingQueue);
  }, [hydrateFittingQueue]);


  // Try-on extraction cache (image+category -> extracted cutout)
  const tryonCacheRef = React.useRef(new Map());

  // Which item is currently being extracted (for UI "Loading..." + disabling)
  const [tryOnLoadingId, setTryOnLoadingId] = useState(null);

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("REACT_APP_API_BASE_URL is not set; using local backend fallback");
}

function toAbsoluteTryOnAssetUrl(url) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE_URL}${url}`;
}

  const [currentOutfit, setCurrentOutfit] = useState({
    top: null,
    bottom: null,
    dress: null,
    outer: null,
  });
  
  // 🔑 Fires ONLY when a garment is actually tried on
  const [tryOnToken, setTryOnToken] = useState(0);

    // --------------------------------
  // TRY ON (authoritative trigger)
  // - extraction happens ONLY here
  // - cached for future use
  // --------------------------------

  function cacheKeyFor(item, view = "front") {
  const url = view === "back" ? (item?.imageBack ?? "") : (item?.image ?? "");
  const cat = String(item?.category ?? "");
  const variant = String(item?.variant ?? "");
  const modelCategory = String(item?.modelCategory ?? "");
  return `${view}::${url}::${cat}::${variant}::${modelCategory}`;
}

function getCatalogTryOnState(item) {
  const tryOn = item?.try_on || item?.tryOn || null;

  const assets = tryOn?.assets || {};
  const generated = tryOn?.asset_generation || tryOn?.assetGeneration || {};

  const frontAsset =
    generated?.front?.asset_url ||
    generated?.front?.assetUrl ||
    assets.front ||
    null;

  const backAsset =
    generated?.back?.asset_url ||
    generated?.back?.assetUrl ||
    assets.back ||
    null;

  return {
    status: tryOn?.status || null,
    frontAsset,
    backAsset,
    hasAnyAsset: !!(frontAsset || backAsset),
  };
}

function isCatalogItemTryOnReady(item) {
  const { status, frontAsset, backAsset } = getCatalogTryOnState(item);

  if (status === "available" && (frontAsset || backAsset)) return true;
  if (status === "front_only" && frontAsset) return true;
  if (status === "back_only" && backAsset) return true;

  return false;
}

function ensureActiveEdit() {
  let id = getActiveEditId();

  if (id) {
    const meta = getEditMeta(id);
    if (meta) return id;
  }

  const meta = createEditMeta({ name: "" });
  setActiveEditId(meta.id);
  return meta.id;
}

function getOutfitSlot(category) {
  const c = String(category || "").toLowerCase().trim();

  if (c === "top") return "top";
  if (c === "dress") return "dress";
  if (c === "outerwear" || c === "outer" || c === "jacket" || c === "coat") {
    return "outer";
  }
  if (
    c === "pants" ||
    c === "skirt" ||
    c === "bottom" ||
    c === "jeans" ||
    c === "trousers" ||
    c === "shorts"
  ) {
    return "bottom";
  }

  return null;
}

const applyTryOn = (item) => {
  setCurrentOutfit((prev) => {
    const updated = { ...prev };
    const slot = getOutfitSlot(item?.category);

    if (!slot) {
      console.warn("[applyTryOn] unknown category", {
        id: item?.id,
        name: item?.name,
        category: item?.category,
      });
      return prev;
    }


    // Toggle off
    if (updated[slot]?.id === item.id) {
      updated[slot] = null;
      return updated;
    }

// -----------------------------
// 🔒 HARD SLOT RULES
// -----------------------------

if (slot === "dress") {
  // Dress is exclusive
  updated.top = null;
  updated.bottom = null;
  updated.outer = null;
}

if (slot === "outer") {
  // Outer can be worn with bottom, but not top or dress
  updated.top = null;
  updated.dress = null;
}

if (slot === "top") {
  // Top cannot coexist with dress or outer
  updated.dress = null;
  updated.outer = null;
}

if (slot === "bottom") {
  // Bottom cannot coexist with dress, but can stay with outer
  updated.dress = null;
}

    updated[slot] = item;

    // ✅ Trigger focus ONLY here
    setTryOnToken((t) => t + 1);

    return updated;
  });
};

  const ensureExtracted = async (item) => {
    const keyFront = cacheKeyFor(item, "front");
    const keyBack = cacheKeyFor(item, "back");


  // Cache hit
    const cachedFront = tryonCacheRef.current.get(keyFront);
    const cachedBack = tryonCacheRef.current.get(keyBack);

if (cachedFront?.extractedUrl || cachedBack?.extractedBackUrl) {
  return {
    ...item,
    extractedUrl: cachedFront?.extractedUrl ?? item.extractedUrl ?? null,
    extractedBackUrl: cachedBack?.extractedBackUrl ?? item.extractedBackUrl ?? null,
    image: cachedFront?.extractedUrl ?? item.extractedUrl ?? null,
    imageBack: cachedBack?.extractedBackUrl ?? item.extractedBackUrl ?? null,
    tryonQuality: cachedFront?.tryonQuality ?? item.tryonQuality ?? null,
  };
}

    // Catalog items must use backend-preprocessed assets only.
    const { frontAsset, backAsset } = getCatalogTryOnState(item);

    if (!isCatalogItemTryOnReady(item)) {
      return null;
    }

const resolvedFrontAsset = toAbsoluteTryOnAssetUrl(frontAsset);
const resolvedBackAsset = toAbsoluteTryOnAssetUrl(backAsset);

const patch = {
  extractedUrl: resolvedFrontAsset ?? null,
  extractedBackUrl: resolvedBackAsset ?? null,
  image: resolvedFrontAsset ?? null,
  imageBack: resolvedBackAsset ?? null,
  tryonQuality: item.tryonQuality ?? null,
};

    if (patch.extractedUrl) {
      tryonCacheRef.current.set(keyFront, {
        extractedUrl: patch.extractedUrl,
        tryonQuality: patch.tryonQuality,
      });
    }

    if (patch.extractedBackUrl) {
      tryonCacheRef.current.set(keyBack, {
        extractedBackUrl: patch.extractedBackUrl,
      });
    }

console.log("[ensureExtracted] using backend try-on assets", {
  id: item.id,
  front: patch.image,
  back: patch.imageBack,
  extractedFront: patch.extractedUrl,
  extractedBack: patch.extractedBackUrl,
});

    setFittingRoomItems((prev) =>
      prev.map((it) => (it.id === item.id ? { ...it, ...patch } : it))
    );

    return { ...item, ...patch };
  };


const handleTryOn = async (item) => {
  const slot = getOutfitSlot(item?.category);

  // Toggle off instantly
  if (slot && currentOutfit?.[slot]?.id === item.id) {
    applyTryOn(item);
    return;
  }

  console.log("[handleTryOn] item", {
    id: item?.id,
    name: item?.name,
    category: item?.category,
    slot,
    image: item?.image,
    imageBack: item?.imageBack,
  });

  try {
    setTryOnLoadingId(item.id);

    const enriched = await ensureExtracted(item);

    if (!enriched) {
      console.warn("[handleTryOn] product unavailable for try-on", {
        id: item?.id,
        status: item?.try_on?.status || item?.tryOn?.status || null,
      });
      return;
    }

    logTryOnStarted({
  productId: enriched.id,
  editId: getActiveEditId() || null,
  surface: "fitting_room",
  category: enriched.category || null,
  variant: enriched.variant || null,
});

console.log("[handleTryOn] enriched item", {
  id: enriched?.id,
  name: enriched?.name,
  category: enriched?.category,
  slot,
  meshUrl: enriched?.meshUrl,
  variant: enriched?.variant,
  image: enriched?.image,
  imageBack: enriched?.imageBack,
  extractedUrl: enriched?.extractedUrl,
  extractedBackUrl: enriched?.extractedBackUrl,
  tryOnStatus: enriched?.try_on?.status || enriched?.tryOn?.status || null,
});

applyTryOn(enriched);
  } finally {
    setTryOnLoadingId(null);
  }
};


  const hasOutfit =
  !!currentOutfit.top ||
  !!currentOutfit.bottom ||
  !!currentOutfit.dress ||
  !!currentOutfit.outer;

// ---------- PUBLIC WINDOW ROUTES ----------
if (isPublic) {
  return (
    <PublicLayout onOpenGuide={reopenIntro}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/moods" element={<MoodsIndex />} />
        <Route path="/moods/:slug" element={<MoodPage />} />
        <Route path="/browse" element={<BrowsePage />} />
        <Route path="/products/:productId" element={<ProductDetailPage />} />
        <Route path="/bag" element={<BagPage />} />
        <Route path="/veya-team/login" element={<VeyaTeamLogin />} />      
      <Route path="/merchant-info" element={<MerchantInfoPage />} />
      <Route
  path="/merchant"
  element={
    <MerchantAuthGuard>
      <MerchantLanding />
    </MerchantAuthGuard>
  }
/>
<Route path="/legal" element={<LegalIndex />} />
<Route path="/legal/privacy" element={<PrivacyPolicy />} />
<Route path="/legal/terms" element={<TermsOfUse />} />
<Route path="/legal/merchant-terms" element={<MerchantTerms />} />
<Route path="/legal/cookies" element={<CookieNotice />} />
<Route path="/legal/licenses" element={<LicenseNotices />} />
<Route path="/merchant/apply" element={<MerchantSignup />} />
<Route path="/merchant/signup" element={<Navigate to="/merchant/apply" replace />} />
<Route path="/merchant/login" element={<MerchantLogin />} />
<Route path="/verify-email-sent" element={<VerifyEmailSent accountType="shopper" />} />
<Route path="/verify-email" element={<VerifyEmailPage accountType="shopper" />} />

<Route
  path="/merchant/verify-email-sent"
  element={<VerifyEmailSent accountType="merchant" />}
/>
<Route
  path="/merchant/verify-email"
  element={<VerifyEmailPage accountType="merchant" />}
/>
<Route
  path="/merchant/dashboard/catalog"
  element={
    <MerchantAuthGuard>
      <MerchantCatalogManager />
    </MerchantAuthGuard>
  }
/>

<Route
  path="/merchant/dashboard/import-csv"
  element={
    <MerchantAuthGuard>
        <MerchantImportCsv />
    </MerchantAuthGuard>
  }
/>
<Route
  path="/veya-team"
  element={
    <VeyaTeamAuthGuard>
      <VeyaTeamShell />
    </VeyaTeamAuthGuard>
  }
>
  <Route index element={<Navigate to="/veya-team/edits" replace />} />
  <Route path="edits" element={<VeyaTeamEdits />} />
  <Route path="merchants" element={<VeyaTeamMerchants />} />
  <Route path="activity" element={<VeyaTeamActivity />} />
</Route>

<Route
  path="/merchant/start"
  element={<Navigate to="/merchant/dashboard/import-csv" replace />}
/>

<Route
  path="/merchant/dashboard"
  element={<Navigate to="/merchant/dashboard/catalog" replace />}
/>

<Route
  path="/merchant/onboarding/boutique-details"
  element={
    <MerchantAuthGuard>
      <OnboardingGuard>
        <MerchantBoutiqueDetails />
      </OnboardingGuard>
    </MerchantAuthGuard>
  }
/>

<Route
  path="/merchant/dashboard/product-readiness"
  element={<Navigate to="/merchant/dashboard/catalog" replace />}
/>

<Route
  path="/merchant/settings"
  element={
    <MerchantAuthGuard>
      <MerchantSettingsPage />
    </MerchantAuthGuard>
  }
/>

        <Route path="/about" element={<About />} />
        <Route
  path="/account/avatar"
  element={
    <RequireShopperAuth>
      <AvatarEditorPage avatar={avatarParams} setAvatar={setAvatarParams} />
    </RequireShopperAuth>
  }
/>
        <Route path="/edits" element={<EditsPage />} />
        <Route path="/edits/:editId" element={<EditViewerPage />} />
        <Route path="/signup" element={<ShopperSignup />} />
        <Route
          path="/account"
          element={
            <RequireShopperAuth>
              <ShopperAccountPage />
            </RequireShopperAuth>
          }
        />
       </Routes>

<VeyaIntroModal
  open={introOpen}
  onClose={() => setIntroOpen(false)}
  onStartBrowsing={() => {
    setIntroOpen(false);
    navigate("/browse");
  }}
/>
    </PublicLayout>
  );
}

return (
  <div className="fitting-room-shell">
  {/* ---------- 3D SCENE ---------- */}
<Canvas
  shadows
  resize={{ scroll: false }}
  gl={{
    antialias: true,
    outputColorSpace: THREE.SRGBColorSpace,
    stencil: true, // ✅ REQUIRED
  }}
  onCreated={({ gl }) => {
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    gl.toneMappingExposure = 1.05; // ✅ HARD RESET
  }}
>

  {/* don’t use #111111 if you want “inviting” */}
  <color attach="background" args={["#2b2b2b"]} />

  <DepthOcclusionProvider active={hasOutfit}>
  <LuxuryRoom
  view={view}
  onToggleView={toggleView}
  avatarParams={avatarParams}
  selectedItems={currentOutfit}
  backView={backView}
  garmentMetricsMap={garmentMetricsMap}
  bodyRefs={BODY_REFS}
/>
  </DepthOcclusionProvider>
</Canvas>




      {/* ---------- TOP BAR ---------- */}
           {/* ---------- HEADER (menu left, wordmark right) ---------- */}
<div
  className="luxury-ui"
  style={{
    pointerEvents: "none",
    justifyContent: "space-between",
  }}
>
  {/* LEFT: menu */}
  <div style={{ position: "relative", pointerEvents: "auto" }}>
    <button className="gold-btn" onClick={() => setMenuOpen((v) => !v)}>
      Menu
    </button>

    {menuOpen && (
<div
  className="veya-menu"
  style={{
    position: "absolute",
    top: "110%",
    left: 0,
    zIndex: 1000,
  }}
>

        {/* clickable link style */}
        <style>{`
          .veya-menu-link {
            display: block;
            padding: 10px 8px;
            border-radius: 12px;
            text-decoration: none;
            color: var(--charcoal);
            font-family: var(--font-body);
            font-weight: 600;
            cursor: pointer;
            user-select: none;
            background: transparent;
            border: none;
            width: 100%;
            text-align: left;
          }
          .veya-menu-link:hover {
            background: rgba(251, 247, 242, 0.55);
          }
          .veya-menu-sub {
            padding-left: 22px;
            font-weight: 500;
            opacity: 0.9;
          }
          .veya-menu-heading {
            margin: 10px 8px 6px;
            font-size: 12px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            opacity: 0.65;
            font-family: var(--font-body);
          }
          .veya-menu-sep {
            height: 1px;
            background: rgba(43, 43, 43, 0.10);
            margin: 10px 0;
          }
          .veya-pill {
            display: inline-block;
            margin-left: 8px;
            font-size: 11px;
            padding: 2px 8px;
            border-radius: 999px;
            border: 1px solid rgba(176, 140, 108, 0.35);
            background: rgba(176, 140, 108, 0.12);
            color: var(--charcoal);
            font-weight: 600;
          }
          .veya-disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }
        `}</style>

{/* IN-FITTING-ROOM */}
<div className="veya-menu-heading">Fitting Room</div>

<div className="veya-menu-link" onClick={() => openDrawer("browse")}>
  Browse
</div>

<div className="veya-menu-link" onClick={() => openDrawer("fittingRoom")}>
  Try on
</div>

<div
  className="veya-menu-link"
  onClick={() => {
    ensureActiveEdit();
    openDrawer("edit");
  }}
>
  Active Edit
</div>

<div className="veya-menu-link" onClick={() => openDrawer("wardrobe")}>
  My Wardrobe
</div>

<div className="veya-menu-sep" />

<button
  type="button"
  className="veya-menu-link"
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpen(false);
    setLeftDrawer(null);
    setRightDrawer(null);
    navigate("/browse", { replace: true });
  }}
  style={{
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
  }}
>
  Leave Fitting Room
</button>
      </div>
    )}
  </div>

 
</div>

{/* ---------- VIEW TOGGLE (fixed viewport control) ---------- */}
<div className="view-toggle-fixed">
  <button className="view-toggle-btn" onClick={toggleView}>
    {view === "front" ? "Back" : "Front"}
  </button>
</div>


      {/* ---------- PANELS ---------- */}
            {/* ---------- PANELS (single active drawer) ---------- */}
     {/* ---------- MOBILE PANEL TABS ---------- */}
<div className="mobile-fitting-tabs">
  <button
    className={`mobile-fitting-tab ${leftDrawer === "browse" ? "is-active" : ""}`}
    onClick={() => {
  setLeftDrawer("browse");
  setRightDrawer(null);
}}
  >
    Browse
  </button>

  <button
    className={`mobile-fitting-tab ${rightDrawer === "fittingRoom" ? "is-active" : ""}`}
    onClick={() => {
  setRightDrawer("fittingRoom");
  setLeftDrawer(null);
}}
  >
    Fitting Room
  </button>

  <button
    className={`mobile-fitting-tab ${rightDrawer === "edit" ? "is-active" : ""}`}
    onClick={() => {
  ensureActiveEdit();
  setRightDrawer("edit");
  setLeftDrawer(null);
}}
  >
    Active Edit
  </button>

  <button
    className={`mobile-fitting-tab ${leftDrawer === "wardrobe" ? "is-active" : ""}`}
    onClick={() => {
  setLeftDrawer("wardrobe");
  setRightDrawer(null);
}}
  >
    Wardrobe
  </button>
</div>
     
      <AvatarEditorPanel
        open={leftDrawer === "avatar"}
        avatar={avatarParams}
        setAvatar={setAvatarParams}
        onClose={closeLeftDrawer}
      />

   <BrowsePanel
  open={leftDrawer === "browse"}
  onClose={closeLeftDrawer}
    onAddToFittingRoom={(item) => {
    addToFittingQueue(item);
    setRightDrawer("fittingRoom");
  }}
  onAddToEdit={(pin) => {
  const editId = ensureActiveEdit();
  addToEdit(
    {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      createdAt: Date.now(),
      ...pin,
    },
    editId
  );
}}
  onRequestOpenEdit={() => {
    openDrawer("edit");
  }}
/>

     <EditPanel
  open={rightDrawer === "edit"}
  onClose={closeRightDrawer}
/>
<MyWardrobePanel
  open={leftDrawer === "wardrobe"}
  onClose={closeLeftDrawer}
  onTryOn={(item) => {
    handleTryOn(item);


  }}
/>



            {rightDrawer === "fittingRoom" && (
        <FittingRoomPanel
          items={fittingRoomItems}
          onClose={closeRightDrawer}
          onRemove={(id) => removeFromFittingQueue(id)}
          onTryOn={handleTryOn}
          tryOnLoadingId={tryOnLoadingId}
          onAddToEdit={(item) => {
  const editId = ensureActiveEdit();

  addToEdit(
    {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      createdAt: Date.now(),
      kind: "image",
      imageUrl: item.image,
      title: item.name,
      sourceUrl: item.productUrl || null,
    },
    editId
  );

  setRightDrawer("edit");
}}
        />
      )}

  
<VeyaIntroModal
  open={introOpen}
  onClose={() => setIntroOpen(false)}
  onStartBrowsing={() => navigate("/browse")}
/>

    </div>
  );
}
